// crewbit-admin/src/components/money/clients/OverridePillTooltip.tsx
//
// Tier 2-UI Phase D-admin §WorldClass.1 — companion to OverridePill.
// Mirrors crewbit-mobile/components/money/clients/OverridePillTooltip.tsx
// body decisions, swapped to web idiom: click-outside-to-dismiss overlay
// (NOT mobile's Modal pageSheet — admin doesn't import RN). Reuses the
// position:fixed scrim + centered card pattern from existing admin
// MarkPaidModal / ConfirmDialog.
//
// Carries human-readable reasoning text from the get_client_defaults RPC
// `reasoning` envelope so operators understand why a pre-filled value was
// chosen.

import { useEffect } from 'react';
import { palette, radii, shadow, spacing, typography } from '../../../styles/tokens';

interface Props {
  visible: boolean;
  title?: string;
  reasoning: string;
  onDismiss: () => void;
}

export default function OverridePillTooltip({ visible, title, reasoning, onDismiss }: Props) {
  // Esc-to-dismiss for keyboard parity with other admin overlays.
  useEffect(() => {
    if (!visible) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [visible, onDismiss]);

  if (!visible) return null;

  return (
    <div
      onClick={onDismiss}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.32)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: palette.white,
          borderRadius: radii.md,
          padding: spacing.lg,
          maxWidth: 420,
          width: '90%',
          boxShadow: shadow.lg,
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.sm,
        }}
      >
        <div
          style={{
            fontSize: typography.size.heading,
            fontWeight: typography.weight.semibold,
            color: palette.g800,
          }}
        >
          {title ?? 'How was this determined?'}
        </div>
        <div
          style={{
            fontSize: typography.size.body,
            color: palette.g700,
            lineHeight: typography.lineHeight.normal,
          }}
        >
          {reasoning}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          style={{
            alignSelf: 'flex-end',
            background: palette.accent,
            color: palette.white,
            border: 'none',
            borderRadius: radii.sm,
            paddingInline: spacing.md,
            paddingBlock: spacing.sm,
            fontSize: typography.size.body,
            fontWeight: typography.weight.semibold,
            fontFamily: typography.fontFamily,
            cursor: 'pointer',
            marginTop: spacing.xs,
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}
